/**
 * The automatic context-rehydration budget — Task 306A §4.2.1.
 *
 * Task file: `tasks/306A-commercial-ux-closure.md` §4.
 *
 * ---------------------------------------------------------------------
 * WHAT IT BOUNDS. After a tenant is activated, `CommercialStandingGate`
 * may find the standing boundary saying `activated` while the
 * `me/context` in hand is still the pre-activation snapshot (no
 * organization, no permissions). One automatic refresh repairs that. This
 * module is the thing that stops "one" turning into "every render,
 * forever".
 *
 * WHY IT IS NOT REACT STATE. `refreshMe()` sets `me/context` to
 * `loading`, `AuthGate` renders its session skeleton for that status, and
 * the gate UNMOUNTS. A `useRef` or `useState` bound would therefore be
 * destroyed by the very refresh it is bounding, and a refresh that came
 * back still non-operational would remount, spend again, and loop.
 *
 * WHY IT IS A SEPARATE MODULE RATHER THAN A CONSTANT IN THE GATE. Two
 * different lifecycles own it, and neither can import the other: the GATE
 * spends it, and the AUTH PROVIDER releases it when the session ends. The
 * gate cannot observe sign-out at all — it lives inside `AuthGate`, which
 * stops rendering children the moment the status is `unauthenticated`, so
 * the gate is already gone. Putting the release in a gate effect looks
 * correct and never runs; a binding review caught exactly that. This
 * module has no React import and no app import, so both sides can depend
 * on it.
 *
 * WHY THE CLAIM IS ATOMIC. `main.tsx` mounts the app inside
 * `<StrictMode>`, and React re-invokes mount effects in development with
 * the SAME render closure. A check-then-act spend ("the decision says
 * rehydrate, so increment and refresh") therefore fires twice and spends
 * two attempts. {@link claimRehydrationAttempt} re-reads the live count
 * and answers whether the caller won, so a replayed effect loses.
 *
 * WHY A MAP AND NOT ONE RECORD. The first version stored a single
 * `{ userId, attempts }` and treated a different id as a reset, so
 * `claim(A) → claim(B) → claim(A)` handed A a second attempt: B's claim
 * had erased A's history. A per-user ceiling has to be stored per user,
 * or it is not one.
 *
 * IDENTITY IS A NON-EMPTY STRING, NEVER `null`. `null` used to be
 * accepted as both "this anonymous caller" and "everybody", which meant a
 * stray `release(null)` cleared a real user's budget. The keyed calls now
 * take `string`; clearing everything is a separate, named function.
 * ---------------------------------------------------------------------
 */

/**
 * One automatic attempt. A second is only ever reachable through a human
 * pressing retry, which releases that user's budget first, so the
 * sequence cannot run away.
 */
export const MAX_AUTOMATIC_REHYDRATIONS = 1;

/**
 * Attempts spent, per authenticated user id.
 *
 * Unbounded in principle; bounded in practice by the number of distinct
 * users who sign in on one tab without signing out, and cleared entirely
 * whenever a session ends.
 */
const attemptsByUser = new Map<string, number>();

/** How many automatic attempts this user has spent in this page load. */
export function rehydrationAttemptsFor(userId: string): number {
  return attemptsByUser.get(userId) ?? 0;
}

/**
 * Take one attempt if any remain. Returns whether the caller got it.
 *
 * ATOMIC BY CONSTRUCTION: the ceiling is re-read here, inside the same
 * synchronous step that increments, so two callers in one tick cannot both
 * win. Callers must perform the refresh ONLY when this returns `true`.
 */
export function claimRehydrationAttempt(
  userId: string,
  max: number = MAX_AUTOMATIC_REHYDRATIONS,
): boolean {
  const spent = rehydrationAttemptsFor(userId);
  if (spent >= max) return false;
  attemptsByUser.set(userId, spent + 1);
  return true;
}

/**
 * Give ONE user their budget back. This is the manual-retry path: a human
 * pressing retry must not hand anybody else a free attempt.
 */
export function releaseRehydrationBudget(userId: string): void {
  attemptsByUser.delete(userId);
}

/**
 * Forget every budget. This is the SESSION-ENDED path — there is no
 * longer a user to key anything to, and the next sign-in, including by the
 * same person, is a fresh attempt by a human.
 *
 * Called from `MeContextProvider` on all three routes to
 * `unauthenticated`: the auth-listener `session === null` branch, the
 * explicit `signOut`, and a `me/context` read that comes back 401.
 */
export function clearRehydrationBudgets(): void {
  attemptsByUser.clear();
}
